/**
 * The Product feedback page's reads and its one write (SA-1305), under `['app-feedback', 'admin', …]`.
 * A save writes the answer into the report's key and refetches the list, so the row's status chip and
 * note mark follow.
 */
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ApiError } from '@/services/apiClient'
import {
  getAdminReport, listAdminReports, patchAdminReport,
  type AdminListQuery, type AdminPatch, type AdminReportDetail,
} from '@/services/helpFeedback/appFeedbackApi'

export const ADMIN_FEEDBACK_KEY = ['app-feedback', 'admin'] as const
const listKey = (q: AdminListQuery) => [...ADMIN_FEEDBACK_KEY, 'list', q] as const
const reportKey = (id: string | null | undefined) => [...ADMIN_FEEDBACK_KEY, 'report', id ?? ''] as const

export function useAdminFeedbackList(q: AdminListQuery) {
  return useQuery({
    queryKey: listKey(q),
    queryFn: () => listAdminReports(q),
    placeholderData: keepPreviousData,
  })
}

export function useAdminFeedbackReport(id: string | null | undefined) {
  return useQuery({
    queryKey: reportKey(id),
    queryFn: () => getAdminReport(id!),
    enabled: Boolean(id),
    staleTime: 0,
    // A report that is gone stays gone: no retry on a 404.
    retry: (count, err) => !isGone(err) && count < 1,
  })
}

export function usePatchAdminReport(id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (patch: AdminPatch) => patchAdminReport(id, patch),
    onSuccess: (answer: AdminReportDetail) => {
      qc.setQueryData(reportKey(id), answer)
      void qc.invalidateQueries({ queryKey: [...ADMIN_FEEDBACK_KEY, 'list'] })
    },
  })
}

/** The report the address names no longer exists. */
export const isGone = (err: unknown) => err instanceof ApiError && err.status === 404
